/**
 * src/net/remotes.ts — rendering and local presence of OTHER players.
 *
 * Replaces the single-player RivalPack in multiplayer: every remote racer
 * (human or server bot) is
 *   - a clone of the hero's composed buggy (shared geometry/materials, so a
 *     few Object3Ds each and no extra GPU memory), tinted per player,
 *   - posed by exponential smoothing toward the last server transform,
 *   - one rival-style dust emitter in the fx feed,
 *   - pushed against locally: when a remote car is inside contact range of the
 *     hero, an impulse separates them (see `contact` below) — real Rapier
 *     bodies per remote would need the pack's servo machinery; this keeps the
 *     bump without that cost. Server bots are authoritative on their own line,
 *     so this only nudges the LOCAL car.
 */
import * as THREE from 'three'
import type { Cosmetics, StateRacer } from './net'

export interface RemoteVisual {
  group: THREE.Group          // buggy clone root
  label: THREE.Sprite         // player name tag
  targetPos: THREE.Vector3
  targetHeading: number
  pos: THREE.Vector3
  heading: number
  speed: number
  s: number
  finished: boolean
}

const UP = new THREE.Vector3(0, 1, 0)
const SMOOTH = 8             // exponential approach rate per second
const CONTACT_RANGE = 3.4    // metres, centre-to-centre
const PUSH_STRENGTH = 2600   // impulse scale for separating the hero

function makeLabel(name: string, cos?: Cosmetics | null): THREE.Sprite {
  const canvas = document.createElement('canvas')
  canvas.width = 256
  canvas.height = 64
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = 'rgba(10,8,6,0.55)'
  ctx.beginPath()
  ctx.roundRect(8, 8, 240, 48, 12)
  ctx.fill()
  ctx.strokeStyle = 'rgba(230,180,110,0.9)'
  ctx.lineWidth = 3
  ctx.stroke()
  // The player's equipped cosmetics ride the plate: badge glyph before the
  // name, bought colour, animated fx. The texture is painted ONCE, so
  // paintNameStyle gets a fixed clock and a rainbow lands on a stable hue
  // instead of whatever millisecond this car happened to spawn on.
  const acct = (window as any).Account
  const decorated = (acct && cos) ? acct.nameLabel(name, cos, false) : name
  const text = String(decorated).slice(0, 16)
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  // measure the LABEL, not the name: the badge widens it past the plate
  let px = 30
  ctx.font = `bold ${px}px ui-monospace, monospace`
  while (px > 16 && ctx.measureText(text).width > 220) {
    px -= 2
    ctx.font = `bold ${px}px ui-monospace, monospace`
  }
  if (acct && cos) acct.paintNameStyle(ctx, cos, '#f2e4c8', 0)
  else ctx.fillStyle = '#f2e4c8'
  ctx.fillText(text, 128, 33)
  ctx.shadowBlur = 0
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: tex, transparent: true, depthTest: false,
  }))
  sprite.scale.set(5.0, 1.25, 1)
  return sprite
}

export class RemoteRacers {
  group = new THREE.Group()
  remotes = new Map<string, RemoteVisual>()

  /** Fx feed entries, one rear-axle contact per remote (rival-style wake). */
  fxEntries: {
    position: THREE.Vector3; quaternion: THREE.Quaternion;
    velocity: THREE.Vector3; speed: number; airborne: boolean; id: string;
    wheels: { point: THREE.Vector3; velocity: THREE.Vector3; grounded: boolean;
              slip: number; load: number; width: number; surface: string }[];
    isRival: boolean;
  }[] = []

  private _tmpQ = new THREE.Quaternion()

  /** Sync from a server state message. Creates/destroys as needed. */
  applyState(racers: StateRacer[], heroId: string): void {
    const seen = new Set<string>()
    for (const r of racers) {
      if (r.id === heroId) continue
      seen.add(r.id)
      let rv = this.remotes.get(r.id)
      if (!rv) continue                     // created via ensure()
      rv.targetPos.set(r.x, r.y, r.z)
      rv.targetHeading = r.h
      rv.speed = r.v
      rv.s = r.s
      rv.finished = r.finished
    }
    for (const id of [...this.remotes.keys()]) {
      if (!seen.has(id)) this.remove(id)
    }
  }

  /** Create visuals for roster entries we have not met yet. */
  ensureAll(roster: { id: string; name: string; spectator: boolean;
                      cosmetics?: Cosmetics | null }[],
            heroVisual: THREE.Group | null): void {
    for (const p of roster) {
      if (p.spectator || this.remotes.has(p.id)) continue
      const vis = heroVisual
        ? (heroVisual.clone(true) as THREE.Group)
        : new THREE.Group()
      this.ensure(p.id, p.name, vis, p.cosmetics)
    }
  }

  /** Create a remote entry once we know its display name. */
  ensure(id: string, name: string, visual: THREE.Group,
         cos?: Cosmetics | null): RemoteVisual {
    let rv = this.remotes.get(id)
    if (rv) return rv
    const label = makeLabel(name, cos)
    label.position.set(0, 3.1, 0)
    visual.add(label)
    this.group.add(visual)
    rv = {
      group: visual,
      label,
      targetPos: new THREE.Vector3(),
      targetHeading: 0,
      pos: new THREE.Vector3(),
      heading: 0,
      speed: 0, s: 0, finished: false,
    }
    this.remotes.set(id, rv)
    this.fxEntries.push({
      position: new THREE.Vector3(),
      quaternion: new THREE.Quaternion(),
      velocity: new THREE.Vector3(),
      speed: 0, airborne: false, id,
      wheels: [{
        point: new THREE.Vector3(), velocity: new THREE.Vector3(),
        grounded: true, slip: 0, load: 6000, width: 0.35,
        surface: 'softSand',
      }],
      isRival: true,
    })
    return rv
  }

  remove(id: string): void {
    const rv = this.remotes.get(id)
    if (!rv) return
    this.group.remove(rv.group)
    const idx = this.fxEntries.findIndex((e) => e.id === id)
    if (idx >= 0) this.fxEntries.splice(idx, 1)
    this.remotes.delete(id)
  }

  /** Snap everything (used right after spawn/bootstrap to kill lerp streaks). */
  snap(): void {
    for (const rv of this.remotes.values()) {
      rv.pos.copy(rv.targetPos)
      rv.heading = rv.targetHeading
    }
  }

  fixedUpdate(dt: number): void {
    const k = 1 - Math.exp(-SMOOTH * dt)
    let i = 0
    for (const rv of this.remotes.values()) {
      rv.pos.lerp(rv.targetPos, k)
      let err = rv.targetHeading - rv.heading
      while (err > Math.PI) err -= Math.PI * 2
      while (err <= -Math.PI) err += Math.PI * 2
      rv.heading += err * k
      rv.group.position.copy(rv.pos)
      rv.group.quaternion.setFromAxisAngle(UP, rv.heading)

      // Feed the dust wake.
      const fx = this.fxEntries[i]
      if (fx) {
        fx.position.copy(rv.pos)
        fx.quaternion.setFromAxisAngle(UP, rv.heading)
        const sh = Math.sin(rv.heading), ch = Math.cos(rv.heading)
        fx.velocity.set(sh * rv.speed, 0, ch * rv.speed)
        fx.speed = rv.speed
        const c = fx.wheels[0]!
        c.point.copy(rv.pos)
        c.point.x -= sh * 1.9
        c.point.z -= ch * 1.9
        c.velocity.copy(fx.velocity)
        c.slip = rv.speed * 0.08
      }
      i++
    }
  }

  /**
   * Local push response against the hero. Returns an impulse vector for the
   * hero body, or null. Called after fixedUpdate with the hero position.
   */
  contact(heroPos: THREE.Vector3, heroSpeed: number): THREE.Vector3 | null {
    for (const rv of this.remotes.values()) {
      const d = rv.pos.distanceTo(heroPos)
      if (d > CONTACT_RANGE || d < 1e-4) continue
      const dir = heroPos.clone().sub(rv.pos).setY(0).normalize()
      const closing = Math.max(2, heroSpeed + rv.speed)
      return dir.multiplyScalar(PUSH_STRENGTH * Math.min(1, closing / 20))
    }
    return null
  }
}
